/**
 * SnapboothNav.tsx — meta-nav globale (au-dessus de toute l'app).
 * Inspirée du standalone Snapbooth Redesign : tabs entre les 5 modes
 * (Dashboard / Éditeur / Live / Kiosque / Variations) + brand.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutGrid, Edit3, Zap, Eye, LogOut, User as UserIcon } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';

interface ModeTab {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: number }>;
}

// A19 : l'onglet « Variations » a été retiré de la nav principale. La page
// VariationsView est un artefact de référence design (comparaison de directions
// de layout), pas une fonctionnalité utilisateur — l'exposer comme un mode à part
// entière était trompeur. La route /variations reste accessible par URL directe.
const TABS: ModeTab[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutGrid },
  { id: 'editor', label: 'Éditeur', path: '/editor', icon: Edit3 },
  { id: 'live', label: 'Live', path: '/live', icon: Zap },
  { id: 'kiosk', label: 'Kiosque', path: '/kiosk', icon: Eye },
];

export const SnapboothNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const isActive = (path: string) => {
    if (path === '/editor') return currentPath === '/editor' || currentPath.startsWith('/editor/');
    return currentPath === path;
  };

  return (
    <header className="sb-mode-switch" role="navigation" aria-label="Modes de l'application">
      <div className="sb-mode-brand">
        <div className="sb-mode-logo-mark" aria-hidden="true">
          S
        </div>
        <div className="sb-mode-logo-text">
          <span className="sb-mode-logo-name">Snapbooth</span>
          <span className="sb-mode-logo-sub">redesign</span>
        </div>
      </div>

      <div className="sb-mode-tabs" role="tablist">
        {TABS.map(tab => {
          const Ico = tab.icon;
          const active = isActive(tab.path);
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active}
              className={`sb-mode-tab ${active ? 'is-active' : ''}`}
              onClick={() => navigate(tab.path)}
            >
              <Ico size={14} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Menu utilisateur + déconnexion (B1 : la déconnexion était inaccessible) */}
      <div className="sb-mode-user" style={{ position: 'relative', marginLeft: 'auto' }}>
        <button
          type="button"
          className="sb-mode-user-btn"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Menu utilisateur"
          onClick={() => setMenuOpen(o => !o)}
        >
          <UserIcon size={14} />
          <span className="sb-mode-user-email">{user?.email ?? 'Compte'}</span>
        </button>
        {menuOpen && (
          <>
            <div
              className="sb-mode-user-backdrop"
              style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="sb-mode-user-menu" role="menu">
              {user?.email && <div className="sb-mode-user-menu-email">{user.email}</div>}
              <button
                type="button"
                role="menuitem"
                className="sb-mode-user-menu-item"
                onClick={handleLogout}
              >
                <LogOut size={14} />
                <span>Se déconnecter</span>
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
};
